import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';

const steps = [
  {
    title: "Discovery & Consultation",
    description: "We start by understanding your business needs, analyzing requirements, and defining the project scope to ensure we build exactly what you need.",
    points: ["Requirement Analysis", "Business Consulting", "Scope Definition", "Timeline Planning"],
    image: "/assest/solutions/Discovery.png"
  },
  {
    title: "Design & Planning",
    description: "Our team creates detailed wireframes, UI/UX designs, and technical architecture to visualize your solution before development begins.",
    points: ["UI/UX Design", "System Architecture", "Prototype Development", "User Flow Mapping"],
    image: "/assest/solutions/Design.png"
  },
  {
    title: "Development",
    description: "We build your solution using cutting-edge technologies, clean coding practices, and robust database design for optimal performance.",
    points: ["Clean Coding", "Database Design", "API Integration", "Module Development"],
    image: "/assest/solutions/Development.png"
  },
  {
    title: "Testing & QA",
    description: "Rigorous testing ensures your software is bug-free, secure, and performs flawlessly under all conditions before going live.",
    points: ["Functional Testing", "Security Audit", "Performance Testing", "User Acceptance"],
    image: "/assest/solutions/Testing.png"
  },
  {
    title: "Deployment & Support",
    description: "We launch your product smoothly and provide ongoing maintenance, training, and 24/7 support to ensure long-term success.",
    points: ["Go-Live Assistance", "User Training", "24/7 Support", "Regular Updates"],
    image: "/assest/solutions/Deployment.png"
  }
];

/* ─── EXACT Bulb Shape SVG (used for both clipPath and visual) ─── */
const BULB_PATH = "M210,20 C320,20 380,90 380,180 C380,250 340,300 290,330 C260,350 250,370 250,390 L250,405 C250,420 232,435 210,435 C188,435 170,420 170,405 L170,390 C170,370 160,350 130,330 C80,300 40,250 40,180 C40,90 100,20 210,20 Z";

const BulbDefs = () => (
  <svg width="0" height="0" className="absolute">
    <defs>
      {/* EXACT clipPath — matches visual bulb perfectly */}
      <clipPath id="bulb-clip-exact" clipPathUnits="userSpaceOnUse">
        <path d={BULB_PATH} />
      </clipPath>

      {/* Glass gradient */}
      <linearGradient id="glass-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
        <stop offset="30%" stopColor="rgba(239,205,134,0.08)" />
        <stop offset="70%" stopColor="rgba(4,106,140,0.1)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.15)" />
      </linearGradient>

      {/* Inner glow */}
      <radialGradient id="inner-glow" cx="50%" cy="35%" r="55%">
        <stop offset="0%" stopColor="rgba(239,205,134,0.3)" />
        <stop offset="50%" stopColor="rgba(239,205,134,0.1)" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>

      {/* Gold glow filter */}
      <filter id="gold-glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="6" result="blur" />
        <feFlood floodColor="#EFCD86" floodOpacity="0.5" result="color" />
        <feComposite in="color" in2="blur" operator="in" result="glow" />
        <feMerge>
          <feMergeNode in="glow" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      {/* Soft shadow */}
      <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000" floodOpacity="0.5" />
      </filter>
    </defs>
  </svg>
);

/* ─── REALISTIC Bulb Component ─── */
const RealisticBulb = ({ activeStep, isVisible }) => {
  const sparkles = [
    { x: 210, y: 40, glow: true },
    { x: 340, y: 130, glow: true },
    { x: 355, y: 230, glow: true },
    { x: 325, y: 330, glow: true },
    { x: 210, y: 410, glow: true },
    { x: 95, y: 330, glow: true },
    { x: 65, y: 230, glow: true },
    { x: 80, y: 130, glow: true },
  ];

  return (
    <div
      className="relative w-[420px] h-[500px] transition-all duration-700 ease-out"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
      }}
    >
      {/* Outer ambient glow */}
      <div className="absolute top-[10%] left-[5%] right-[5%] h-[70%] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(239,205,134,0.15)_0%,transparent_70%)] blur-2xl animate-pulse" style={{ animationDuration: '3s' }} />

      {/* IMAGE CONTAINER — Perfectly clipped to bulb shape */}
      <div className="absolute top-[3%] left-[8%] right-[8%] h-[78%]">
        {/* Exact bulb clip */}
        <svg width="100%" height="100%" viewBox="0 0 420 460" preserveAspectRatio="xMidYMid slice" className="absolute inset-0">
          <g clipPath="url(#bulb-clip-exact)">
            <AnimatePresence mode="wait">
              <motion.image
                key={activeStep}
                href={steps[activeStep].image}
                x="0" y="0" width="420" height="460"
                preserveAspectRatio="xMidYMid slice"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              />
            </AnimatePresence>
            {/* Inner shadow overlay for depth */}
            <rect x="0" y="0" width="420" height="460" fill="url(#inner-glow)" opacity="0.6" />
            <rect x="0" y="0" width="420" height="460" fill="url(#glass-grad)" opacity="0.4" />
          </g>
        </svg>
      </div>

      {/* VISUAL BULB SVG — Exact same shape as clipPath */}
      <svg viewBox="0 0 420 500" className="absolute inset-0 w-full h-full pointer-events-none" filter="url(#soft-shadow)">

        {/* Glass body */}
        <path
          d={BULB_PATH}
          fill="url(#glass-grad)"
          stroke="#EFCD86"
          strokeWidth="2"
          filter="url(#gold-glow)"
        />

        {/* Inner glow layer */}
        <path d={BULB_PATH} fill="url(#inner-glow)" opacity="0.5" />

        {/* Glass reflection highlight (top-left) */}
        <ellipse cx="150" cy="100" rx="50" ry="80" fill="rgba(255,255,255,0.12)" transform="rotate(-25 150 100)" filter="blur(6px)" />
        <ellipse cx="165" cy="115" rx="20" ry="35" fill="rgba(255,255,255,0.25)" transform="rotate(-25 165 115)" />

        {/* Glass reflection (bottom-right subtle) */}
        <ellipse cx="300" cy="300" rx="30" ry="50" fill="rgba(239,205,134,0.08)" transform="rotate(15 300 300)" filter="blur(8px)" />

        {/* Metal screw base (holder) */}
        <rect x="165" y="435" width="90" height="14" rx="3" fill="none" stroke="#8B7355" strokeWidth="2" />
        <rect x="170" y="449" width="80" height="12" rx="2" fill="none" stroke="#8B7355" strokeWidth="1.5" />
        <rect x="175" y="461" width="70" height="10" rx="2" fill="none" stroke="#8B7355" strokeWidth="1.5" />

        {/* Screw threads */}
        <line x1="168" y1="435" x2="252" y2="435" stroke="#EFCD86" strokeWidth="1" opacity="0.6" />
        <line x1="170" y1="442" x2="250" y2="442" stroke="#EFCD86" strokeWidth="1" opacity="0.5" />
        <line x1="172" y1="449" x2="248" y2="449" stroke="#EFCD86" strokeWidth="1" opacity="0.4" />
        <line x1="174" y1="456" x2="246" y2="456" stroke="#EFCD86" strokeWidth="1" opacity="0.3" />

        {/* Contact point at bottom */}
        <circle cx="210" cy="478" r="8" fill="none" stroke="#8B7355" strokeWidth="2" />
        <circle cx="210" cy="478" r="3" fill="#EFCD86" opacity="0.8" />

        {/* Sparkles — only active one glows */}
        {sparkles.map((pos, i) => (
          <g key={i}>
            {/* Glow ring for active */}
            {activeStep === i && (
              <circle
                cx={pos.x} cy={pos.y} r="14"
                fill="none" stroke="#5BAA5A" strokeWidth="1.5"
                opacity="0.4" className="animate-ping"
                style={{ animationDuration: '2s' }}
              />
            )}
            {/* Main dot */}
            <circle
              cx={pos.x} cy={pos.y}
              r={activeStep === i ? 5 : 2}
              fill={activeStep === i ? '#5BAA5A' : '#4a7c49'}
              opacity={activeStep === i ? 1 : 0.5}
              style={{
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: activeStep === i ? 'drop-shadow(0 0 8px #5BAA5A)' : 'none'
              }}
            />
            {/* Cross sparkle for active */}
            {activeStep === i && (
              <>
                <line x1={pos.x - 10} y1={pos.y} x2={pos.x + 10} y2={pos.y} stroke="#5BAA5A" strokeWidth="1" opacity="0.5" />
                <line x1={pos.x} y1={pos.y - 10} x2={pos.x} y2={pos.y + 10} stroke="#5BAA5A" strokeWidth="1" opacity="0.5" />
              </>
            )}
          </g>
        ))}

        {/* Step indicator dots at bottom */}
        <g transform="translate(210, 495)">
          {steps.map((_, i) => (
            <circle
              key={i}
              cx={(i - 2) * 14} cy="0"
              r={activeStep === i ? 4 : 2}
              fill={activeStep === i ? '#EFCD86' : '#374151'}
              style={{ transition: 'all 0.4s ease' }}
            />
          ))}
        </g>
      </svg>
    </div>
  );
};

const Solutions = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [bulbVisible, setBulbVisible] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    const mainObserver = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => setBulbVisible(entry.isIntersecting)); },
      { threshold: 0.1 }
    );
    const stepObserver = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => { if (entry.isIntersecting) setActiveStep(parseInt(entry.target.dataset.step)); }); },
      { threshold: 0.5 }
    );

    if (mainRef.current) mainObserver.observe(mainRef.current);
    document.querySelectorAll('.step-section').forEach((section) => stepObserver.observe(section));

    return () => { mainObserver.disconnect(); stepObserver.disconnect(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628] text-white relative overflow-hidden selection:bg-[#EFCD86]/30">
      <BulbDefs />

      <div className="fixed inset-0 z-0 bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] pointer-events-none" />

      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none opacity-30">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-[#EFCD86] rounded-full"
            style={{
              left: `${10 + (i * 8) % 80}%`, top: `${15 + (i * 13) % 70}%`,
              animation: `float ${12 + i * 2}s infinite ease-in-out`, animationDelay: `${i * 0.8}s`,
              opacity: 0.3 + (i % 3) * 0.1
            }} />
        ))}
      </div>

      <Navbar />

      <main className="relative z-10">
        {/* Hero */}
        <section className="relative pt-32 pb-20 flex items-center justify-center text-center min-h-[70vh]">
          <div className="absolute inset-0 z-0 bg-[url('/assest/logo.jfif')] bg-cover bg-center bg-fixed opacity-20 grayscale" />
          <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0a1628]/70 via-[#0a1628]/90 to-[#0a1628]" />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative z-10 px-4">
            <span className="text-[#EFCD86] text-sm font-bold uppercase tracking-[0.3em] mb-6 block">OUR PROCESS</span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">How We <span className="text-[#EFCD86]">Work</span></h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-12">From idea to deployment in 5 simple steps</p>
            <motion.div className="flex flex-col items-center gap-2" animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
              <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll</span>
              <div className="w-5 h-8 border-2 border-[#EFCD86]/30 rounded-full flex justify-center pt-1.5">
                <div className="w-0.5 h-2 bg-[#EFCD86]/60 rounded-full" />
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Main Content */}
        <div ref={mainRef} className="relative">

          {/* LEFT: Steps */}
          <div className="w-full lg:w-1/2 relative z-10">
            {steps.map((step, i) => (
              <div key={i} className="step-section min-h-screen flex items-center px-8 md:px-16 lg:pl-24 py-16 relative" data-step={i}>

                {/* Connector Line */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute left-8 md:left-16 top-[60%] bottom-[-20%] w-1 z-20">
                    <div className="absolute inset-0 w-full bg-gray-800/40 rounded-full" />
                    <motion.div
                      className="absolute top-0 w-full rounded-full"
                      initial={{ height: '0%', opacity: 0 }}
                      animate={{ height: activeStep === i ? '100%' : '0%', opacity: activeStep === i ? 1 : 0 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      style={{
                        background: 'linear-gradient(to bottom, #EFCD86, rgba(239,205,134,0.3))',
                        boxShadow: '0 0 15px rgba(239,205,134,0.6), 0 -5px 20px rgba(239,205,134,0.3)'
                      }}
                    />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 transition-all duration-500"
                      style={{
                        backgroundColor: activeStep > i ? '#EFCD86' : '#1e293b',
                        borderColor: activeStep > i ? '#EFCD86' : '#475569',
                        boxShadow: activeStep === i ? '0 0 10px rgba(239,205,134,0.6)' : 'none',
                        transform: `translateX(-50%) scale(${activeStep === i ? 1.2 : 1})`
                      }}
                    />
                  </div>
                )}

                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.3 }}
                  className="max-w-lg relative"
                >
                  {/* Step Number with Glow */}
                  <div className="relative mb-6 inline-block">
                    {activeStep === i && (
                      <motion.div layoutId="stepGlow" className="absolute inset-0 bg-[#EFCD86] blur-3xl opacity-30 rounded-full scale-150" transition={{ duration: 0.5 }} />
                    )}
                    <span className={`relative text-7xl md:text-9xl font-black transition-all duration-500 block ${activeStep === i
                      ? 'text-[#EFCD86] drop-shadow-[0_0_30px_rgba(239,205,134,0.8)] drop-shadow-[0_0_60px_rgba(239,205,134,0.4)]'
                      : 'text-gray-800/50'
                      }`}>
                      0{i + 1}
                    </span>
                    <motion.div initial={false} animate={{ opacity: activeStep === i ? 1 : 0, y: activeStep === i ? 0 : 10 }} className="absolute -bottom-2 left-0">
                      <span className="text-[#EFCD86] text-xs font-bold uppercase tracking-widest bg-[#0a1628]/90 px-3 py-1.5 rounded border border-[#EFCD86]/30 shadow-[0_0_15px_rgba(239,205,134,0.2)]">Current Step</span>
                    </motion.div>
                  </div>

                  <div className="block lg:hidden my-6 rounded-xl overflow-hidden border border-[#EFCD86]/20">
                    <img src={step.image} alt={step.title} className="w-full aspect-video object-cover" />
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{step.title}</h2>
                  <p className="text-gray-400 text-lg leading-relaxed mb-6">{step.description}</p>

                  <ul className="space-y-3 relative">
                    {step.points.map((point, idx) => (
                      <li key={idx} className={`flex items-start gap-3 transition-all duration-300 ${activeStep === i ? 'text-gray-200' : 'text-gray-500'}`}>
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 transition-all duration-500 ${activeStep === i ? 'bg-[#EFCD86] shadow-[0_0_8px_rgba(239,205,134,0.8)]' : 'bg-gray-700'}`} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>

          {/* RIGHT: Sticky Realistic Bulb */}
          <div className="hidden lg:block fixed right-4 xl:right-12 top-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <RealisticBulb activeStep={activeStep} isVisible={bulbVisible} />
          </div>

        </div>

        <div className="h-[20vh]" />
      </main>

      <div className="relative z-10"><Footer /></div>

      <style>{`@keyframes float { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,-15px)} }`}</style>
    </div>
  );
};

export default Solutions;